import { useEffect, useId, useState, type FormEvent } from 'react';
import { Carte, MessageErreur } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { useReglerReferentHandicap, type VueAccessibiliteParametrage } from '../../lib/useParametrage';
import { CLASSE_BOUTON_ENREGISTRER, mentionReglage, messageErreur } from './commun';

const CLASSE_CHAMP =
  'mt-1 min-h-[44px] w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20';

const CLASSE_BOUTON_SECONDAIRE =
  'inline-flex min-h-[44px] items-center justify-center rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50 disabled:opacity-50';

/**
 * Référent handicap (TF-341) : la personne que l'écran « Accessibilité »
 * présente à tous pour une demande d'aménagement. Un nom et au moins un moyen
 * de contact ; tout effacer le retire et la carte disparaît de l'écran.
 */
export function OngletAccessibilite({ accessibilite }: { accessibilite: VueAccessibiliteParametrage }): JSX.Element {
  const toast = useToast();
  const regler = useReglerReferentHandicap();
  const id = useId();
  const { referent, limites } = accessibilite;
  const depart = {
    nom: referent?.nom ?? '',
    email: referent?.email ?? '',
    telephone: referent?.telephone ?? '',
  };
  const [saisie, setSaisie] = useState(depart);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(
    () => setSaisie({ nom: referent?.nom ?? '', email: referent?.email ?? '', telephone: referent?.telephone ?? '' }),
    [referent?.nom, referent?.email, referent?.telephone],
  );

  const inchange =
    saisie.nom.trim() === depart.nom && saisie.email.trim() === depart.email && saisie.telephone.trim() === depart.telephone;

  const enregistrer = (valeurs: typeof saisie, message: string): void => {
    setErreur(null);
    regler.mutate(valeurs, {
      onSuccess: () => toast(message),
      onError: (refus) => setErreur(messageErreur(refus, 'Le référent handicap n’a pas pu être enregistré.')),
    });
  };

  const envoyer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    enregistrer(saisie, 'Référent handicap enregistré');
  };

  const champ = (cle: keyof typeof saisie, valeur: string): void => {
    setErreur(null);
    setSaisie((avant) => ({ ...avant, [cle]: valeur }));
  };

  return (
    <Carte titre="Référent handicap">
      <form onSubmit={envoyer} className="space-y-4 px-4 py-4 sm:px-6">
        <p className="text-sm text-codialis-gray">
          Affiché à tous sur l’écran « Accessibilité » : l’interlocuteur d’un apprenant ou d’un candidat pour
          une demande d’aménagement de son parcours. Le nom et au moins un moyen de contact.{' '}
          {referent ? mentionReglage({ parDefaut: false, ...accessibilite }) : 'Aucun référent : la carte n’est pas affichée.'}
        </p>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="min-w-0">
            <label htmlFor={`${id}-nom`} className="text-xs font-semibold text-codialis-navy">
              Nom et prénom
            </label>
            <input
              id={`${id}-nom`}
              type="text"
              autoComplete="name"
              maxLength={limites.nom}
              value={saisie.nom}
              onChange={(evenement) => champ('nom', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </div>
          <div className="min-w-0">
            <label htmlFor={`${id}-email`} className="text-xs font-semibold text-codialis-navy">
              Adresse e-mail
            </label>
            <input
              id={`${id}-email`}
              type="email"
              autoComplete="email"
              maxLength={limites.email}
              value={saisie.email}
              onChange={(evenement) => champ('email', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </div>
          <div className="min-w-0">
            <label htmlFor={`${id}-telephone`} className="text-xs font-semibold text-codialis-navy">
              Téléphone
            </label>
            <input
              id={`${id}-telephone`}
              type="tel"
              autoComplete="tel"
              maxLength={limites.telephone}
              value={saisie.telephone}
              onChange={(evenement) => champ('telephone', evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          {referent && (
            <button
              type="button"
              className={CLASSE_BOUTON_SECONDAIRE}
              disabled={regler.isPending}
              onClick={() =>
                enregistrer({ nom: '', email: '', telephone: '' }, 'Référent handicap retiré : la carte n’est plus affichée')
              }
            >
              Retirer
            </button>
          )}
          <button type="submit" className={CLASSE_BOUTON_ENREGISTRER} disabled={regler.isPending || inchange}>
            {regler.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>

        {erreur && <MessageErreur message={erreur} />}
      </form>
    </Carte>
  );
}
